/** The plugin bundle on the CDN. Loaded as a module, so it may sit anywhere on the page. */
export const PLUGIN_SCRIPT_URL = 'https://cdn.schenkwerk.com/schenkwerk-voucher.js';

/**
 * How the shop appears on the customer's website:
 * - `widget`: a floating "Gutschein kaufen" button (bottom right) that opens the shop as an overlay
 * - `inline`: the shop rendered in place, inside the page content
 */
export type EmbedVariant = 'widget' | 'inline';

/** One highlighted piece of a code line. */
export interface CodeToken {
  text: string;
  kind: 'tag' | 'attr' | 'value' | 'plain';
}

export type CodeLine = CodeToken[];

const tag = (text: string): CodeToken => ({ text, kind: 'tag' });
const attr = (text: string): CodeToken => ({ text, kind: 'attr' });
const value = (text: string): CodeToken => ({ text, kind: 'value' });
const plain = (text: string): CodeToken => ({ text, kind: 'plain' });

/**
 * The snippet a customer pastes into their website, as highlighted lines.
 *
 * Script and element go together in one place: `type="module"` scripts are
 * deferred by the browser wherever they sit, and the custom element upgrades
 * once the script has defined it. The same module URL is only ever evaluated
 * once, so pasting the snippet twice on a page is harmless.
 */
export function embedSnippet(storeId: string, variant: EmbedVariant): CodeLine[] {
  const element: CodeLine[] =
    variant === 'widget'
      ? [
          [tag('<schenkwerk-voucher')],
          [plain('  '), attr('storeId'), plain('='), value(`"${storeId}"`)],
          [plain('  '), attr('asWidget'), tag('>')],
          [tag('</schenkwerk-voucher>')],
        ]
      : [
          [tag('<schenkwerk-voucher')],
          [plain('  '), attr('storeId'), plain('='), value(`"${storeId}"`), tag('>')],
          [tag('</schenkwerk-voucher>')],
        ];

  return [
    [tag('<script'), plain(' '), attr('type'), plain('='), value('"module"')],
    [plain('  '), attr('src'), plain('='), value(`"${PLUGIN_SCRIPT_URL}"`), tag('>')],
    [tag('</script>')],
    [],
    ...element,
  ];
}

/** The snippet as plain text — exactly what the code block shows. */
export function snippetText(lines: CodeLine[]): string {
  return lines.map((line) => line.map((token) => token.text).join('')).join('\n') + '\n';
}
